<template>
  <PurchaseHeader>
    <div class="bento-item articles">
      <CustomLoader ref="loader"/>

      <!-- Ce que le catalogue représente : combien d'articles, ce qui est en stock, ce qui est parti sur des dossiers -->
      <div class="kpis">
        <div class="kpi">
          <span class="kpi-icon"><span class="fas fa-tags"></span></span>
          <div class="kpi-text">
            <strong>{{ articles.length }}</strong>
            <span>article{{ articles.length > 1 ? 's' : '' }} au catalogue</span>
            <small>{{ totals.inStockCount }} en stock<template v-if="totals.pendingCount > 0"> · {{ totals.pendingCount }} à approvisionner</template></small>
          </div>
        </div>
        <div class="kpi">
          <span class="kpi-icon"><span class="fas fa-boxes-stacked"></span></span>
          <div class="kpi-text">
            <strong>{{ formatAmount(totals.stockValue) }}</strong>
            <span>en stock (HT)</span>
            <small>pas encore affecté à un dossier</small>
          </div>
        </div>
        <div class="kpi">
          <span class="kpi-icon"><span class="fas fa-folder-open"></span></span>
          <div class="kpi-text">
            <strong>{{ formatAmount(totals.allocatedValue) }}</strong>
            <span>affecté aux dossiers (HT)</span>
            <small>{{ orders.length }} dossier{{ orders.length > 1 ? 's' : '' }} concerné{{ orders.length > 1 ? 's' : '' }}</small>
          </div>
        </div>
        <div class="kpi">
          <span class="kpi-icon"><span class="fas fa-cart-shopping"></span></span>
          <div class="kpi-text">
            <strong>{{ formatAmount(totals.boughtValue) }}</strong>
            <span>acheté (HT)</span>
            <small>factures validées, avoirs non déduits</small>
          </div>
        </div>
      </div>

      <div class="toolbar">
        <div v-if="orderValues" class="switch">
          <button type="button" :class="{active: mode === 'ARTICLES'}" @click="mode = 'ARTICLES'">Par article</button>
          <button type="button" :class="{active: mode === 'ORDERS'}" @click="mode = 'ORDERS'">Par dossier</button>
        </div>
        <div class="search">
          <span class="fas fa-magnifying-glass"></span>
          <input v-model="query" type="search" :placeholder="mode === 'ORDERS' ? 'Dossier, article...' : 'Article, référence...'" />
        </div>
        <button v-if="regularizableCount > 0" type="button" class="row-button" title="Rattacher aux achats arrivés ce qui était affecté aux dossiers au-delà du stock" @click="regularize()"><span class="fas fa-rotate"></span>Régulariser</button>
        <button type="button" class="row-button primary" @click="$refs.articleModal.openModal()"><span class="fas fa-plus"></span>Nouvel article</button>
      </div>

      <!-- Par article : quantités achetées, reçues, en stock et affectées, avec le détail par dossier -->
      <div v-if="mode === 'ARTICLES'" class="panel">
        <p v-if="articles.length === 0" class="empty">Aucun article au catalogue. Ajoutez-en un ici, ou depuis une ligne d'achat (« Enregistrer dans le catalogue »).</p>
        <p v-else-if="shownArticles.length === 0" class="empty">Aucun résultat.</p>
        <table v-else class="stats">
          <thead>
            <tr>
              <th class="chevron-col"></th>
              <th class="sortable" @click="setSort('name')">Article{{ sortMark('name') }}</th>
              <th class="num hide-small sortable" @click="setSort('boughtCostHt')">Acheté{{ sortMark('boughtCostHt') }}</th>
              <th class="num hide-small sortable" @click="setSort('allocatedCost')">Affecté aux dossiers{{ sortMark('allocatedCost') }}</th>
              <th class="num sortable" @click="setSort('inStockQuantity')">En stock{{ sortMark('inStockQuantity') }}</th>
              <th class="num sortable" @click="setSort('stockValueHt')">Valeur du stock{{ sortMark('stockValueHt') }}</th>
              <th class="actions-col"></th>
            </tr>
          </thead>
          <tbody>
            <template v-for="article in shownArticles" :key="article.articleId">
              <tr class="article-row" :class="{open: isOpen(article)}" @click="toggle(article)">
                <td class="chevron-col"><span class="chevron" :class="{open: isOpen(article)}"><span class="fas fa-chevron-right"></span></span></td>
                <td>
                  <strong class="name">{{ article.name }}</strong>
                  <span class="muted">{{ subtitleOf(article) }}</span>
                </td>
                <td class="num hide-small">
                  {{ formatQuantity(article.boughtQuantity, article.unit) }}
                  <span class="muted">{{ formatAmount(article.boughtCostHt) }}</span>
                </td>
                <td class="num hide-small">
                  {{ formatQuantity(article.allocatedQuantity, article.unit) }}
                  <span class="muted">{{ formatAmount(allocatedCost(article)) }}</span>
                </td>
                <td class="num">
                  <strong :class="{zero: article.inStockQuantity === 0, negative: article.inStockQuantity < 0}">{{ formatQuantity(article.inStockQuantity, article.unit) }}</strong>
                  <span v-if="article.pendingQuantity > 0" class="muted pending" title="Affecté à des dossiers au-delà du stock">{{ formatQuantity(article.pendingQuantity, article.unit) }} à approvisionner</span>
                </td>
                <td class="num">{{ formatAmount(article.stockValueHt) }}</td>
                <td class="actions-col">
                  <button v-if="canRegularize(article)" type="button" class="icon-button" title="Régulariser : rattacher aux achats arrivés ce qui était affecté au-delà du stock" @click.stop="regularize(article)"><span class="fas fa-rotate"></span></button>
                  <button v-if="orderValues && !article.deactivated" type="button" class="icon-button" title="Affecter à un dossier" @click.stop="$refs.assignArticlesModal.openModal({orderValues, articleId: article.articleId})"><span class="fas fa-folder-open"></span></button>
                  <button v-if="!article.deactivated" type="button" class="icon-button" title="Modifier" @click.stop="$refs.articleModal.openModal(article)"><span class="fas fa-pen-to-square"></span></button>
                  <button v-if="!article.deactivated" type="button" class="icon-button" title="Supprimer" @click.stop="remove(article)"><span class="fas fa-trash-can"></span></button>
                </td>
              </tr>
              <tr v-if="isOpen(article)" class="detail-row">
                <td colspan="7">
                  <div class="detail">
                    <ul class="facts">
                      <li><span>Reçu</span><strong>{{ formatQuantity(article.receivedQuantity, article.unit) }}</strong></li>
                      <li><span>Rendu aux fournisseurs</span><strong>{{ formatQuantity(article.returnedQuantity, article.unit) }}</strong></li>
                      <li v-if="article.pendingQuantity > 0"><span>À approvisionner</span><strong class="negative">{{ formatQuantity(article.pendingQuantity, article.unit) }}</strong></li>
                      <li><span>Lignes d'achat en stock</span><strong>{{ article.stockLineCount }}</strong></li>
                      <li v-if="article.unitPriceHt !== undefined && article.unitPriceHt !== null"><span>Prix habituel HT</span><strong>{{ formatAmount(article.unitPriceHt) }}</strong></li>
                      <li v-if="article.vatRate !== undefined && article.vatRate !== null"><span>TVA habituelle</span><strong>{{ formatRate(article.vatRate) }}</strong></li>
                    </ul>
                    <p v-if="article.deactivated" class="removed">Cet article a été supprimé du catalogue ; ses achats existants restent comptés.</p>
                    <p v-if="article.notes" class="notes">{{ article.notes }}</p>

                    <h3>Affecté aux dossiers</h3>
                    <p v-if="article.byOrder.length === 0" class="muted">Aucune quantité n'est affectée à un dossier.</p>
                    <ul v-else class="orders">
                      <li v-for="order in article.byOrder" :key="order.orderId">
                        <span class="order-name" :title="orderLabel(order.orderId)">
                          <span class="order-label">{{ orderLabel(order.orderId) }}</span>
                          <router-link v-if="orderValues" :to="'/dossiers/' + order.orderId" class="goto" title="Ouvrir le dossier"><span class="fas fa-up-right-from-square"></span></router-link>
                        </span>
                        <span class="bar"><span :style="{width: shareOf(article, order) + '%'}"></span></span>
                        <span class="order-quantity">
                          {{ formatQuantity(order.quantity, article.unit) }}
                          <small v-if="order.pendingQuantity > 0" class="pending">+ {{ formatQuantity(order.pendingQuantity, article.unit) }} à approvisionner</small>
                        </span>
                        <span class="order-cost muted">{{ formatAmount(order.costHt) }}</span>
                      </li>
                    </ul>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>

      <!-- Par dossier : les articles du catalogue que chaque dossier a consommés -->
      <div v-else class="panel">
        <p class="hint">Seuls les achats dont les lignes sont rattachées à un article du catalogue figurent ici.</p>
        <p v-if="shownOrders.length === 0" class="empty">{{ query ? 'Aucun résultat.' : 'Aucune quantité n\'est encore affectée à un dossier. Affectez du stock depuis la Logistique, ou ventilez un achat sur un dossier.' }}</p>
        <article v-for="order in shownOrders" :key="order.orderId" class="order-card">
          <header>
            <strong class="order-title">
              {{ order.label }}
              <router-link :to="'/dossiers/' + order.orderId" class="goto" title="Ouvrir le dossier"><span class="fas fa-up-right-from-square"></span></router-link>
            </strong>
            <span class="order-total">{{ formatAmount(order.costHt) }} HT</span>
          </header>
          <ul>
            <li v-for="line in order.lines" :key="line.articleId">
              <span class="line-name">{{ line.name }}</span>
              <span class="line-quantity">
                {{ formatQuantity(line.quantity, line.unit) }}
                <small v-if="line.pendingQuantity > 0" class="pending">+ {{ formatQuantity(line.pendingQuantity, line.unit) }} à approvisionner</small>
              </span>
              <span class="line-cost muted">{{ formatAmount(line.costHt) }}</span>
            </li>
          </ul>
        </article>
      </div>
    </div>

    <ArticleModal ref="articleModal" :callback="loadData" />
    <AssignArticlesModal ref="assignArticlesModal" :callback="loadData" />
    <ConfirmModal ref="deleteConfirmModal" title="Suppression" text="Supprimer cet article du catalogue ? Il ne doit plus être utilisé par un achat." yes="Supprimer" no="Retour"/>
  </PurchaseHeader>
</template>

<script>
import {library} from '@fortawesome/fontawesome-svg-core';
import {faBoxesStacked, faCartShopping, faChevronRight, faFolderOpen, faMagnifyingGlass, faPenToSquare, faPlus, faRotate, faTags, faTrashCan, faUpRightFromSquare} from "@fortawesome/free-solid-svg-icons";
import CustomLoader from "@/components/CustomLoader.vue";
import ConfirmModal from "@/components/modals/confirmModal.vue";
import {NotificationService} from "@vues/v-common";
import PurchaseHeader from "@/purchase/components/PurchaseHeader.vue";
import ArticleModal from "@/purchase/components/modals/ArticleModal.vue";
import AssignArticlesModal from "@/purchase/components/modals/AssignArticlesModal.vue";
import {ArticleController} from "@/purchase/controllers/ArticleController";
import {LogisticsController} from "@/purchase/controllers/LogisticsController";
import {loadOrderValues} from "@/purchase/services/OrderChoiceService";
import {PURCHASE_CATEGORY_ENUM, formatAmount, formatQuantity, formatRate, labelOf, round2} from "@/purchase/utils";
library.add(faBoxesStacked, faCartShopping, faChevronRight, faFolderOpen, faMagnifyingGlass, faPenToSquare, faPlus, faRotate, faTags, faTrashCan, faUpRightFromSquare);

// Catalogue des articles achetés (une planche, une vis, un tissu...) et leurs statistiques de stock : tout se lit sur les lignes d'achat
// rattachées à un article, rien n'est saisi en double. Le stock total d'un article s'additionne d'un achat à l'autre, et se répartit
// par dossier.
export default {
  name: 'ArticleView',
  components: {PurchaseHeader, CustomLoader, ConfirmModal, ArticleModal, AssignArticlesModal},
  data() {
    return {
      articles: [],
      orderValues: undefined,   // dossiers de l'entreprise, absents sans l'option production
      mode: 'ARTICLES',         // ARTICLES : un article par ligne ; ORDERS : un dossier par carte
      query: '',
      sortKey: 'name',
      sortDesc: false,
      openedIds: [],            // articles dont le détail est déplié
    };
  },
  async mounted() {
    this.orderValues = await loadOrderValues();
    await this.loadData();
  },
  computed: {
    orderLabels() {
      return new Map((this.orderValues ?? []).map(order => [String(order.value), order.label]));
    },
    totals() {
      const sum = (field) => this.articles.reduce((total, article) => total + (Number(article[field]) || 0), 0);
      return {
        stockValue: round2(sum('stockValueHt')),
        boughtValue: round2(sum('boughtCostHt')),
        allocatedValue: round2(this.articles.reduce((total, article) => total + this.allocatedCost(article), 0)),
        inStockCount: this.articles.filter(article => article.inStockQuantity > 0).length,
        pendingCount: this.articles.filter(article => article.pendingQuantity > 0).length,
      };
    },
    normalizedQuery() {
      return this.normalize(this.query);
    },
    shownArticles() {
      const query = this.normalizedQuery;
      const articles = query
          ? this.articles.filter(article => this.matches(query, [article.name, article.reference, labelOf(PURCHASE_CATEGORY_ENUM, article.category, '')]))
          : [...this.articles];
      const direction = this.sortDesc ? -1 : 1;
      return articles.sort((a, b) => {
        if (this.sortKey === 'name') return direction * String(a.name).localeCompare(String(b.name), 'fr');
        return direction * (this.sortValue(a) - this.sortValue(b));
      });
    },
    // Un dossier avec ce qu'il a pris de chaque article, du plus coûteux au moins coûteux
    orders() {
      const byOrder = new Map();
      this.articles.forEach(article => article.byOrder.forEach(order => {
        if (!byOrder.has(order.orderId)) byOrder.set(order.orderId, {orderId: order.orderId, label: this.orderLabel(order.orderId), costHt: 0, lines: []});
        const entry = byOrder.get(order.orderId);
        entry.costHt = round2(entry.costHt + order.costHt);
        entry.lines.push({articleId: article.articleId, name: article.name, unit: article.unit, quantity: order.quantity, pendingQuantity: order.pendingQuantity, costHt: order.costHt});
      }));
      byOrder.forEach(order => order.lines.sort((a, b) => b.costHt - a.costHt));
      return [...byOrder.values()].sort((a, b) => b.costHt - a.costHt);
    },
    // Articles dont des quantités affectées au-delà du stock peuvent être couvertes par le stock arrivé depuis
    regularizableCount() {
      return this.articles.filter(article => this.canRegularize(article)).length;
    },
    shownOrders() {
      const query = this.normalizedQuery;
      if (!query) return this.orders;
      return this.orders.filter(order => this.matches(query, [order.label, ...order.lines.map(line => line.name)]));
    },
  },
  methods: {
    formatAmount,
    formatQuantity,
    formatRate,
    normalize(text) {
      return String(text ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
    },
    matches(query, texts) {
      return texts.some(text => this.normalize(text).includes(query));
    },
    canRegularize(article) {
      return article.pendingQuantity > 0 && article.inStockQuantity + article.pendingQuantity > 0;
    },
    // Rattache aux achats arrivés ce qui était affecté au-delà du stock (un article, ou tous)
    async regularize(article) {
      const res = await LogisticsController.regularize(article ? {articleId: article.articleId} : {});
      if (res && !res.errors) {
        const done = res.regularizedQuantity > 0;
        (new NotificationService).addNotification(done ? "success" : "warning", done ? "Quantités régularisées sur les achats" : "Le stock ne couvre pas encore ces quantités");
        await this.loadData();
      }
    },
    allocatedCost(article) {
      return round2(article.byOrder.reduce((sum, order) => sum + order.costHt, 0));
    },
    sortValue(article) {
      return this.sortKey === 'allocatedCost' ? this.allocatedCost(article) : Number(article[this.sortKey]) || 0;
    },
    setSort(key) {
      if (this.sortKey === key) {
        this.sortDesc = !this.sortDesc;
      } else {
        this.sortKey = key;
        this.sortDesc = key !== 'name';
      }
    },
    sortMark(key) {
      return this.sortKey === key ? (this.sortDesc ? ' ▼' : ' ▲') : '';
    },
    subtitleOf(article) {
      return [article.reference, labelOf(PURCHASE_CATEGORY_ENUM, article.category, ''), article.deactivated ? 'supprimé' : ''].filter(Boolean).join(' · ');
    },
    orderLabel(orderId) {
      return this.orderLabels.get(String(orderId)) ?? 'Dossier';
    },
    // Part de l'article acheté (net des retours) qui est partie sur ce dossier, en %
    shareOf(article, order) {
      const net = article.boughtQuantity - article.returnedQuantity;
      return net > 0 ? Math.max(0, Math.min(100, Math.round(order.quantity / net * 100))) : 0;
    },
    isOpen(article) {
      return this.openedIds.includes(article.articleId);
    },
    toggle(article) {
      this.openedIds = this.isOpen(article) ? this.openedIds.filter(id => id !== article.articleId) : [...this.openedIds, article.articleId];
    },
    async loadData() {
      this.$refs.loader?.startLoading();
      const stats = await ArticleController.getStats();
      this.articles = stats && !stats.errors ? stats : [];
      this.$refs.loader?.stopLoading();
    },
    async remove(article) {
      const res = await this.$refs.deleteConfirmModal.displayChoice(() => ArticleController.delete(article.articleId));
      if (res !== false && !(res && res.errors)) {
        (new NotificationService).addNotification("success", "Article supprimé");
        await this.loadData();
      }
    },
  }
}
</script>

<style lang="stylus" scoped>
@require '~/src/common/style/common.styl';

h1
  color THEME_COLOR_PRIMARY

  span
    color THEME_COLOR_PRIMARY_FADDED

.articles
  position relative
  display flex
  flex-direction column
  gap 18px

.muted
  color #6f7a93
  font-size 13px

// Chiffres clés du catalogue
.kpis
  display grid
  grid-template-columns repeat(4, minmax(0, 1fr))
  gap 12px

  .kpi
    display flex
    align-items center
    gap 12px
    padding 12px 16px
    border 1px solid #e3e7ef
    border-radius 12px
    background #f8f9fd

  .kpi-icon
    display flex
    align-items center
    justify-content center
    flex 0 0 auto
    width 38px
    height 38px
    border-radius 10px
    background #eef2fb
    color THEME_COLOR_PRIMARY

  .kpi-text
    display flex
    flex-direction column
    min-width 0
    line-height 1.25

    strong
      font-size 17px
      color #2b3550

    span
      font-size 13px
      color #4a5670

    small
      font-size 12px
      color #8a93a8

.toolbar
  display flex
  flex-wrap wrap
  align-items center
  gap 12px

  .switch
    display inline-flex
    padding 3px
    border 1px solid #e0e5f0
    border-radius 10px
    background #f4f6fb

    button
      height 30px
      padding 0 14px
      border none
      border-radius 8px
      background transparent
      color #4a5670
      font-size 13px
      font-weight 600
      cursor pointer

      &.active
        background white
        color THEME_COLOR_PRIMARY
        box-shadow 0 1px 3px rgba(0, 0, 0, .1)

  .search
    display flex
    align-items center
    gap 8px
    margin-left auto
    padding 0 12px
    height 38px
    min-width 240px
    border 1px solid #e0e5f0
    border-radius 10px
    background white
    color #8a93a8

    input
      flex 1
      min-width 0
      border none
      outline none
      background transparent
      font-size 14px

.panel
  display flex
  flex-direction column
  gap 10px

.hint
  margin 0
  font-size 12.5px
  color #8a93a8

.empty
  margin 0
  padding 26px 12px
  border 1px dashed #d6dcea
  border-radius 12px
  text-align center
  font-size 13.5px
  color #8a93a8

// Tableau des articles
.stats
  width 100%
  border-collapse collapse

  th
    padding 8px 10px
    border-bottom 1px solid #e3e7ef
    text-align left
    font-size 12px
    font-weight 600
    color #8a93a8
    text-transform uppercase
    letter-spacing .03em
    white-space nowrap

    &.sortable
      cursor pointer
      user-select none

      &:hover
        color THEME_COLOR_PRIMARY

  td
    padding 10px
    border-bottom 1px solid #eef1f7
    vertical-align middle
    font-size 14px

  .num
    text-align right
    font-variant-numeric tabular-nums

    .muted
      display block

  .chevron-col
    width 28px
    padding-right 0

  .actions-col
    width 110px
    white-space nowrap
    text-align right

  .article-row
    cursor pointer

    &:hover, &.open
      background #f6f8fd

    .name
      display block
      color #2b3550

    .muted
      display block

    .zero
      color #a0a8bb
      font-weight 500

    .negative
      color #c0392b

.negative
  color #c0392b

.pending
  display block
  font-size 11.5px
  font-weight 600
  color #9a6b00

.chevron
  display inline-block
  color #8a93a8
  font-size 11px
  transition transform .15s ease

  &.open
    transform rotate(90deg)

.icon-button
  width 28px
  height 28px
  border none
  border-radius 7px
  background transparent
  color #6f7a93
  cursor pointer

  &:hover
    background #eef2fb
    color THEME_COLOR_PRIMARY

// Détail d'un article : ce qui est arrivé, ce qui est en stock, et où le reste est parti
.detail-row
  background #fafbfe

  > td
    padding 4px 10px 16px 38px

.detail
  display flex
  flex-direction column
  gap 8px

  h3
    margin 6px 0 0
    font-size 13px
    color #2b3550

  .facts
    display flex
    flex-wrap wrap
    gap 8px 24px
    margin 0
    padding 0
    list-style none

    li
      display flex
      flex-direction column
      line-height 1.3

    span
      font-size 12px
      color #8a93a8

    strong
      font-size 14px
      color #2b3550
      font-variant-numeric tabular-nums

  .removed
    margin 0
    font-size 12.5px
    color #9a6b00

  .notes
    margin 0
    font-size 13px
    color #4a5670

  .orders
    display flex
    flex-direction column
    gap 6px
    margin 0
    padding 0
    list-style none

    li
      display grid
      grid-template-columns minmax(120px, 2fr) minmax(80px, 3fr) 110px 90px
      align-items center
      gap 12px
      font-size 13.5px

    .order-name
      display flex
      align-items center
      gap 8px
      min-width 0

    .order-label
      overflow hidden
      white-space nowrap
      text-overflow ellipsis

    .order-quantity, .order-cost
      text-align right
      font-variant-numeric tabular-nums

    .bar
      height 6px
      border-radius 3px
      background #e8ecf5
      overflow hidden

      span
        display block
        height 100%
        border-radius 3px
        background #7f97d6

// Vue par dossier
.order-card
  padding 12px 14px
  border 1px solid #e6eaf2
  border-left 4px solid #9db0dd
  border-radius 12px
  background #fbfcfe

  header
    display flex
    align-items baseline
    justify-content space-between
    gap 10px

    strong
      color #2b3550

  .order-total
    font-weight 600
    font-variant-numeric tabular-nums

  ul
    display flex
    flex-direction column
    gap 4px
    margin 8px 0 0
    padding 0
    list-style none

  li
    display grid
    grid-template-columns minmax(0, 1fr) 120px 100px
    gap 12px
    font-size 13.5px

  .line-name
    overflow hidden
    white-space nowrap
    text-overflow ellipsis

  .line-quantity, .line-cost
    text-align right
    font-variant-numeric tabular-nums

.goto
  display inline-flex
  align-items center
  justify-content center
  flex 0 0 auto
  width 22px
  height 22px
  border-radius 6px
  color #8a93a8
  font-size 11px
  text-decoration none

  &:hover
    background #eef2fb
    color THEME_COLOR_PRIMARY

.row-button
  display inline-flex
  align-items center
  gap 6px
  height 34px
  padding 0 14px
  border 1px solid #e0e5f0
  border-radius 8px
  background white
  color #4a5670
  font-size 13px
  font-weight 600
  cursor pointer
  transition all .15s ease

  &:hover
    background #eef2fb
    border-color #c5d0ea
    color THEME_COLOR_PRIMARY

  &.primary
    border-color THEME_COLOR_PRIMARY
    background THEME_COLOR_PRIMARY
    color THEME_COLOR_PRIMARY_OPPOSITE

    &:hover
      background CTRL_PRIMARY_HOVER

  span
    font-size 11px

@media (max-width 1000px)
  .kpis
    grid-template-columns repeat(2, minmax(0, 1fr))

  .toolbar .search
    margin-left 0
    flex 1

@media (max-width 700px)
  .kpis
    grid-template-columns minmax(0, 1fr)

  .stats .hide-small
    display none

  .detail .orders li
    grid-template-columns minmax(0, 1fr) 90px 80px

    .bar
      display none
</style>
